/**
 * @file CreateShipmentSheet.tsx
 * @description Сайдбар «Новая отгрузка» (phase10 → phase19.24 шаг 5.2,
 * замечание пользователя 2026-09-22 «давай информативнее, в сайдбаре, нужен
 * клиент, посмотри что требуется»).
 *
 * Перевозчику для заявки нужны получатель с телефоном, точка, тариф и места
 * с весом — всё это уже есть в заказе, и сайдбар ПОКАЗЫВАЕТ, а не спрашивает:
 * секции получателя, «куда», тарифа, редактируемые места (из расчёта) и
 * чек-лист готовности. Поля перевозчика открываются только когда условий нет
 * или менеджер сознательно жмёт «Изменить перевозчика».
 *
 * MVP: в отгрузку входят все позиции заказа в полном количестве.
 */

import { useEffect, useMemo, useState } from "react";

import { SdekPickupPointPicker } from "./SdekPickupPointPicker";
import { ShipmentCarrierOverride } from "./ShipmentCarrierOverride";
import { ShipmentPackagesEditor } from "./ShipmentPackagesEditor";
import { ShipmentReadinessList } from "./ShipmentReadinessList";
import { ShipmentSheetSummary } from "./ShipmentSheetSummary";
import { deliveryAgreementErrorText, type OrderDeliveryReceiptDto } from "../../lib/api-delivery-agreement";
import { createShipment, type OrderDeliveryDto, type ShipmentCarrier } from "../../lib/api-orders";
import { createPackageDraft, toNumber, type DeliveryPackageDraft } from "../../lib/delivery-quote";
import { shipmentReadiness } from "../../lib/shipment-readiness";
import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";

interface Props {
  orderId: string;
  orderNumber: string;
  delivery: OrderDeliveryDto | null;
  receipt: OrderDeliveryReceiptDto | null;
  defaultCarrier?: ShipmentCarrier | null;
  pickupOnly?: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/** Стартовые места: из расчёта доставки, иначе одно место со всем составом. */
function initialPackages(delivery: OrderDeliveryDto | null, receipt: OrderDeliveryReceiptDto | null) {
  const allIds = (delivery?.items ?? []).map((i) => i.orderItemId);
  const quoted = receipt?.quote?.packages ?? [];
  if (quoted.length > 0) {
    return quoted.map((p, i) =>
      createPackageDraft({
        weightG: String(p.weightGrams),
        lengthMm: p.lengthMm ? String(p.lengthMm) : "",
        widthMm: p.widthMm ? String(p.widthMm) : "",
        heightMm: p.heightMm ? String(p.heightMm) : "",
        orderItemIds: i === 0 ? allIds : [],
      }),
    );
  }
  const total = (delivery?.items ?? []).reduce(
    (sum, i) => sum + (i.weightG ?? 0) * i.quantity,
    0,
  );
  return [createPackageDraft({ weightG: total > 0 ? String(Math.round(total)) : "", orderItemIds: allIds })];
}

export function CreateShipmentSheet({
  orderId,
  orderNumber,
  delivery,
  receipt,
  defaultCarrier,
  pickupOnly,
  onClose,
  onCreated,
}: Props) {
  const prefilled = !!receipt && receipt.method !== "PICKUP" && !pickupOnly;
  const carrierName =
    receipt?.method === "SHOP_COURIER" ? "курьер магазина" : (receipt?.carrierName ?? "ТК");
  const [override, setOverride] = useState(!prefilled);
  const [carrier, setCarrier] = useState<ShipmentCarrier>(defaultCarrier ?? "PICKUP");
  const [trackNumber, setTrackNumber] = useState("");
  const [pickupPointCode, setPickupPointCode] = useState<string | null>(
    receipt?.quote?.destination.pickupPointCode ?? receipt?.pickupPointCode ?? null,
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [packages, setPackages] = useState<DeliveryPackageDraft[]>(() =>
    initialPackages(delivery, receipt),
  );
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Данные доставки могли доехать позже открытия сайдбара — места ставим по ним.
  useEffect(() => {
    setPackages(initialPackages(delivery, receipt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delivery?.items.length, receipt?.quote?.quotedAt]);

  const checks = useMemo(
    () => shipmentReadiness({ delivery, receipt, packages }),
    [delivery, receipt, packages],
  );
  const items = delivery?.items ?? [];

  async function onSubmit() {
    setError(null);
    if (items.length === 0) {
      setError("В заказе нет товарных позиций.");
      return;
    }
    setSaving(true);
    try {
      await createShipment(orderId, {
        ...(override ? { carrier, trackNumber: trackNumber.trim() || undefined } : {}),
        // ПВЗ шлём, только если сменили: иначе сервер берёт согласованный.
        ...(pickupPointCode && pickupPointCode !== (receipt?.pickupPointCode ?? null)
          ? { pickupPointCode }
          : {}),
        items: items.map((i) => ({ orderItemId: i.orderItemId, quantity: String(i.quantity) })),
        packages: packages
          .filter((p) => (toNumber(p.weightG) ?? 0) > 0)
          .map((p, i) => ({
            number: i + 1,
            weightG: Math.round(toNumber(p.weightG) ?? 0),
            lengthMm: toNumber(p.lengthMm),
            widthMm: toNumber(p.widthMm),
            heightMm: toNumber(p.heightMm),
            comment: p.comment.trim() || null,
            orderItemIds: p.orderItemIds,
          })),
        notes: notes.trim() || undefined,
      });
      onCreated();
    } catch (err) {
      setError(deliveryAgreementErrorText(err, "Не удалось создать отгрузку"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full max-w-none gap-0 md:max-w-2xl">
        <SheetHeader className="shrink-0">
          <SheetTitle>Новая отгрузка · {orderNumber}</SheetTitle>
          <SheetDescription>
            Заявка перевозчику по согласованным условиям. Проверьте получателя и места — остальное
            подставится само.
          </SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto py-2">
          {error ? <Alert severity="error">{error}</Alert> : null}
          {pickupOnly ? (
            <Alert severity="warning" title="Заказ на самовывоз">
              Клиент забирает заказ в пункте магазина — отгрузка перевозчиком не нужна.
            </Alert>
          ) : null}
          {prefilled && receipt ? (
            <ShipmentSheetSummary
              recipient={delivery?.recipient ?? null}
              receipt={receipt}
              carrierName={receipt.carrierName ?? carrierName}
              pickupPointCode={pickupPointCode}
              canChangePickup={receipt.carrier === "SDEK" && !saving}
              onChangePickup={() => setPickerOpen(true)}
            />
          ) : (
            <p className="text-sm text-on-surface-variant">
              Условия доставки ещё не согласованы — перевозчика и места укажите вручную.
            </p>
          )}
          <ShipmentPackagesEditor value={packages} onChange={setPackages} items={items} disabled={saving} />
          {prefilled ? <ShipmentReadinessList checks={checks} carrierName={carrierName} /> : null}
          <ShipmentCarrierOverride
            open={override}
            carrier={carrier}
            trackNumber={trackNumber}
            disabled={saving}
            onOpen={() => setOverride(true)}
            onCarrier={setCarrier}
            onTrackNumber={setTrackNumber}
          />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sh-notes">Заметки</Label>
            <textarea
              id="sh-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="rounded-md bg-input px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="sticky bottom-0 flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-outline-variant bg-surface-lowest/95 pt-3 backdrop-blur">
          <Button variant="outline" className="min-h-12" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button className="min-h-12" onClick={onSubmit} disabled={saving}>
            {saving ? "Создание..." : "Создать отгрузку"}
          </Button>
        </div>
      </SheetContent>
      <SdekPickupPointPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(point) => {
          setPickupPointCode(point.externalCode);
          setPickerOpen(false);
        }}
      />
    </Sheet>
  );
}
